import { useSessionStorage } from '@cheqroom/hooks';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDoClearSession } from '../../api/clear-session.api';
import { DiscoveryResponse, doDiscovery, OrganisationWithWorkspaces } from '../../api/discovery.api';
import { Step } from '../../App';

export interface SelectOrganisationForLoginStepState {
	state?: {
		organisations?: OrganisationWithWorkspaces[];
		email?: string;
		error?: string;
	};
}

export interface SelectOrganisationForLoginData {
	organisations: OrganisationWithWorkspaces[];
	email?: string;
	error?: string;
	selectOrganisation: (organisationId: string) => void;
	changeEmail: () => void;
}

export const useSelectOrganisationForLogin = (): SelectOrganisationForLoginData => {
	const navigateTo = useNavigate();
	const { state } = useLocation() as SelectOrganisationForLoginStepState;
	const [, saveOrganisationInSession] = useSessionStorage<string>('organisation');

	// Must clear the identity cookie, not just navigate: EmailStep auto-forwards while it exists.
	const { mutate: clearSession } = useDoClearSession({
		onSettled: () => navigateTo(Step.ENTER_EMAIL, { replace: true }),
	});

	const selectOrganisation = (organisationId: string) => {
		saveOrganisationInSession(organisationId);
		navigateTo(Step.SELECT_WORKSPACE, { replace: true, state: { organisationId } });
	};

	return {
		organisations: state?.organisations ?? [],
		email: state?.email,
		error: state?.error,
		selectOrganisation,
		changeEmail: () => clearSession(),
	};
};

/**
 * "Back" from a workspace's login step: re-reads the unscoped identity and, only when it has more
 * than one org, drops any scope and returns to the org picker. Anything else - a single org, the
 * join-an-organisation flow, an error - runs the caller's `fallback`, i.e. its original behaviour.
 * Decided from the data, not the `organisation` session key: SelectOrganisationStep (join) writes the
 * key too, and a multi-org user arriving by deep link or a recent-workspace link has none.
 */
export const useReturnToOrganisationPicker = () => {
	const navigateTo = useNavigate();
	const [organisationIdFromSession, , clearOrganisationInSession] = useSessionStorage<string | undefined>(
		'organisation'
	);

	const goToPickerIfMultiOrg = ({ email, organisations }: DiscoveryResponse): boolean => {
		if (!organisations || organisations.length <= 1) {
			return false;
		}
		clearOrganisationInSession();
		navigateTo(Step.SELECT_ORGANISATION_FOR_LOGIN, { replace: true, state: { organisations, email } });
		return true;
	};

	/** `discovery`: the caller's own discovery result - already the full identity when no org is stored. */
	const returnToOrganisationPicker = (fallback: () => void, discovery?: DiscoveryResponse) => {
		if (!organisationIdFromSession) {
			if (!discovery || !goToPickerIfMultiOrg(discovery)) {
				fallback();
			}
			return;
		}

		void doDiscovery({})
			.then((identity) => {
				if (!goToPickerIfMultiOrg(identity)) {
					fallback();
				}
			})
			.catch(fallback);
	};

	return { returnToOrganisationPicker };
};

/** Whether a "Back to organizations" control should be shown: scoped to an org, and genuinely multi-org. */
export const useCanReturnToOrganisationPicker = (): boolean => {
	const [organisationIdFromSession] = useSessionStorage<string | undefined>('organisation');

	// Own key, not ['discovery']: that one is shared by every discovery call regardless of scope.
	const { data } = useQuery(['identity-unscoped'], ({ signal }) => doDiscovery({}, { signal }), {
		enabled: !!organisationIdFromSession,
		retry: false,
		cacheTime: 0,
	});

	return !!organisationIdFromSession && (data?.organisations?.length ?? 0) > 1;
};
